import { useMutation, useQuery, useQueryClient } from 'react-query';
import { bffInstance } from '../api/axiosInstance';
import useUserSettingStore from './UserSettingStore';

export type OperatorType = "gt" | "gte" | "lt" | "lte" | "eq" | "btw";

export interface IThresholdCondition {
    operator: OperatorType;
    value: number;
    color: string;
    label: string;
}

export interface IWidgetThreshold {
    type: "absolute" | "percentage";
    conditions: IThresholdCondition[];
}

export interface IWidgetField {
    name: string;
    threshold?: IWidgetThreshold;
}

export interface IDashboardWidget {
    id: string;
    view?: string;
    fields: IWidgetField[];
}

export interface IDashboardFilter {
    department?: string;
    storeIds?: string[];
    group?: any;
    company?: any[];
    accounts?: any[];
    date?: string;
    reportDate?: string | null;
    reportDaysAgo?: number;
    agingReportDate?: string | null;
    agingReportDaysAgo?: number;
    scheduleCodes?: any[];
    startDate?: string | null;
    endDate?: string | null;
    dateRangePreset?: number | null;
}

export interface IDashboardConfig {
    id: string;
    filter?: IDashboardFilter;
    widgets?: IDashboardWidget[];
}

export interface IUserSettings {
    loginId: string;
    dashboard: IDashboardConfig[];
}

export interface IDashboardWidgetAccessSetting {
    id: string;
    permissions: string[];
}

export interface IDashboardAccessSetting {
    dashboardId: string;
    dashboardName: string;
    roles: string[];
    widgets?: IDashboardWidgetAccessSetting[];
}


export const fetchUserSettings = async () => {
    const res = await bffInstance.get<IUserSettings>('/getUserSettings');
    return res.data;
};

export const updateUserSettings = async ( settings: IUserSettings) => {
    const res = await bffInstance.put<IUserSettings>(
        '/updateUserSettings',
        settings
    );
    return res.data;
};

export function useGetUserSetting(enterpriseId: string, storeId: string) {

    const {setUserSetting, setUserDashboardAccessSetting, setExemptedUsers} = useUserSettingStore();

    const setting = useQuery({
        queryKey: ['getUserSettings'],
        queryFn: () =>fetchUserSettings(),
        onSuccess(data) {
            setUserSetting(data as any)
        },
    });

    const dashboardAccessSetting =  useQuery({
        queryKey: ['getDashboardAccessSettings', enterpriseId, storeId],
        queryFn: () => fetchDashboardAccessSettingsByUser(enterpriseId, storeId),
        onSuccess(data:any) {
            setUserDashboardAccessSetting(data?.domains as any || []);
            setExemptedUsers(data?.exemptedUsers as any || []);
        },
        enabled: !!enterpriseId && !!storeId,
    });

    return {
        isLoading: setting.isLoading || dashboardAccessSetting.isLoading,
        error: setting.error || dashboardAccessSetting.error, 
    };
}

export const useUpdateUserSettings = ({ onSuccess, onError }: any = {}) => {
    const queryClient = useQueryClient();

    const mutation = useMutation(
        (settings: IUserSettings) => updateUserSettings(settings), 
        {
            onError: (error: any) => {
                if (onError) onError(error);
                console.error("Failed to update user settings:", error);
            },
            onSuccess: (data: any) => {
                queryClient.invalidateQueries(['getUserSettings']);
                if (onSuccess) onSuccess(data);
            },
        }
    );

    return { mutation };
};

export const getAnalysisData = async ( data: any) => {
    const res = await bffInstance.post<string>(
        '/getDashboardAnalysis',
        data
    );
    return res.data;
};

const fetchDashboardAccessSettingsByUser = async (enterpriseId: string, storeId: string) => {
  const res = await bffInstance.get<Record<any, any>[]>(`/dashboard/getDashboardAccessByUser/${enterpriseId}/${storeId}`);
  return res.data;
};
